import {
  DTypeDTO,
  DTypeDTOCapacityEnum,
  DTypeDTOFireProtectionTypeEnum,
  DTypeDTOHeightEnum,
  DTypeDTOLengthEnum,
  DTypeDTOTypeEnum,
  MTypeDTO,
  MTypeDTOCapacityEnum,
  MTypeDTOFireProtectionTypeEnum,
  MTypeDTOHeightEnum,
  MTypeDTOLengthEnum,
  MTypeDTOTypeEnum,
  ShearForceTypeDTO,
  ShearForceTypeDTOCapacityEnum,
  ShearForceTypeDTOFireProtectionTypeEnum,
  ShearForceTypeDTOHeightEnum,
  ShearForceTypeDTOLengthEnum,
  ShearForceTypeDTOTypeEnum,
  ShortElementTypeDTO,
  ShortElementTypeDTOCapacityEnum,
  ShortElementTypeDTOFireProtectionTypeEnum,
  ShortElementTypeDTOHeightEnum,
  ShortElementTypeDTOLengthEnum,
  ShortElementTypeDTOTypeEnum,
  SMVariantDTO,
  SteelSMTypeDTO,
  SteelSMTypeDTOCapacityEnum,
  SteelSMTypeDTOHeightEnum,
  SteelSMTypeDTOLengthEnum,
  SteelSMTypeDTOTypeEnum,
  SteelSQTypeDTO,
  SteelSQTypeDTOCapacityEnum,
  SteelSQTypeDTOHeightEnum,
  SteelSQTypeDTOLengthEnum,
  SteelSQTypeDTOTypeEnum,
  VarHVMTypeDTO,
  VarHVMTypeDTOVariantEnum,
  VarUVMTypeDTO,
  VarUVMTypeDTOVariantEnum,
  VarWOMTypeDTO,
  VarWOMTypeDTOVariantEnum,
  VarWUMTypeDTO,
  VarWUMTypeDTOVariantEnum,
} from '~/api/webservice/index';

type ValueOf<T extends object> = T[keyof T];

/** */
export const ISOPROType = Object.freeze({
  ...MTypeDTOTypeEnum,
  ...ShearForceTypeDTOTypeEnum,
  ...ShortElementTypeDTOTypeEnum,
  ...DTypeDTOTypeEnum,
  ...SteelSMTypeDTOTypeEnum,
  ...SteelSQTypeDTOTypeEnum
});

/** */
export type ISOPROType = ValueOf<typeof ISOPROType>;

export type HeightOffsetTypeProductDto = VarHVMTypeDTO | VarUVMTypeDTO | VarWOMTypeDTO | VarWUMTypeDTO;

export type BendingMomentTypeProductDto = MTypeDTO | DTypeDTO | HeightOffsetTypeProductDto;

export type SteelTypeProductDto = SteelSMTypeDTO | SteelSQTypeDTO;

export type ISOPROProductDTO =
  | BendingMomentTypeProductDto
  | ShearForceTypeDTO
  | ShortElementTypeDTO
  | SteelSQTypeDTO
  | SteelSMTypeDTO
  | SMVariantDTO;

export const HeightOffsetVariantType = Object.freeze({
  ...VarHVMTypeDTOVariantEnum,
  ...VarUVMTypeDTOVariantEnum,
  ...VarWOMTypeDTOVariantEnum,
  ...VarWUMTypeDTOVariantEnum
});

export type HeightOffsetVariantTypes = ValueOf<typeof HeightOffsetVariantType>;

/** */
export const ISOPROHeight = Object.freeze({
  ...DTypeDTOHeightEnum,
  ...MTypeDTOHeightEnum,
  ...ShearForceTypeDTOHeightEnum,
  ...ShortElementTypeDTOHeightEnum,
  ...SteelSMTypeDTOHeightEnum,
  ...SteelSQTypeDTOHeightEnum
});

/** */
export type ISOPROHeight = ValueOf<typeof ISOPROHeight>;

/** */
export const ISOPROLoadBearingCapacity = Object.freeze({
  ...DTypeDTOCapacityEnum,
  ...MTypeDTOCapacityEnum,
  ...ShearForceTypeDTOCapacityEnum,
  ...ShortElementTypeDTOCapacityEnum,
  ...SteelSMTypeDTOCapacityEnum,
  ...SteelSQTypeDTOCapacityEnum
});

/** */
export type ISOPROLoadBearingCapacity = ValueOf<
  typeof ISOPROLoadBearingCapacity
>;

/** */
export const ISOPROFireProtection = Object.freeze({
  ...DTypeDTOFireProtectionTypeEnum,
  ...MTypeDTOFireProtectionTypeEnum,
  ...ShearForceTypeDTOFireProtectionTypeEnum,
  ...ShortElementTypeDTOFireProtectionTypeEnum,
  // there is no fp for steel types
});

/** */
export type ISOPROFireProtection = ValueOf<typeof ISOPROFireProtection>;

/** */
export enum ISOPROConcreteCovering {
  /** 35 mm */
  cv35 = 35,
  /** 50 mm
   * @deprecated Not considered for the current state of the project
   */
  cv50 = 50,
}

const ISOPROLength = Object.freeze({
  ...DTypeDTOLengthEnum,
  ...MTypeDTOLengthEnum,
  ...ShearForceTypeDTOLengthEnum,
  ...ShortElementTypeDTOLengthEnum,
  ...SteelSMTypeDTOLengthEnum,
  ...SteelSQTypeDTOLengthEnum
});

/** */
export type ISOPROLengths = ValueOf<typeof ISOPROLength>;

/** */
export enum ISOPROShearForceCapacityEnum {
  /** */
  Q4 = 4,
  /** */
  Q6 = 6,
  /** */
  Q8 = 8,
  /** */
  Q10 = 10,
  /** */
  // Q12 = 12,
  /** */
  Q = 'Q',
}

/** Common single values for **ISOPRO D** and **ISOPRO M** types */
type CommonShearForceCapacities =
  | ISOPROShearForceCapacityEnum.Q6
  | ISOPROShearForceCapacityEnum.Q8
  | ISOPROShearForceCapacityEnum.Q10;

type CommonSteelTypeShearForceCapacities =
  | ISOPROShearForceCapacityEnum.Q8
  | ISOPROShearForceCapacityEnum.Q10
  // | ISOPROShearForceCapacityEnum.Q12

type ISOPROShearForceCapacityFor = {
  /** For **ISOPRO D** type */
  D: [CommonShearForceCapacities];
  /** For **ISOPRO M** type */
  M:
  | [ISOPROShearForceCapacityEnum.Q4 | CommonShearForceCapacities]
  | [
    ISOPROShearForceCapacityEnum.Q4 | ISOPROShearForceCapacityEnum.Q8,
    ISOPROShearForceCapacityEnum.Q4
  ]
  | [
    ISOPROShearForceCapacityEnum.Q6,
    ISOPROShearForceCapacityEnum.Q6 | ISOPROShearForceCapacityEnum.Q4
  ];
  Variants: [ISOPROShearForceCapacityEnum.Q4 | ISOPROShearForceCapacityEnum.Q6];
  // SQ: [CommonSteelTypeShearForceCapacities];
  SM:
  | [CommonSteelTypeShearForceCapacities]
  | [ISOPROShearForceCapacityEnum.Q]
};

/** */
export type ISOPROShearForceCapacity<
  Type extends
  DTypeDTOTypeEnum
  | MTypeDTOTypeEnum
  | "Variants"
  | SteelSMTypeDTOTypeEnum
> = ISOPROShearForceCapacityFor[Type];

export type ISOPROShearForceCapacityCode<
  Type extends
  DTypeDTOTypeEnum
  | MTypeDTOTypeEnum
  | "Variants"
  | SteelSMTypeDTOTypeEnum,
  A extends ISOPROShearForceCapacity<Type> = ISOPROShearForceCapacity<Type>
> = A extends never
  ? never
  : `Q${A[0]}${A[1] extends number ? `Q${A[1]}` : ""}`;

export type IProductProperties = {
  dimensions: {
    length: NonNullable<ISOPROProductDTO["length"]>;
    readonly insulationThickness: number;
    readonly concreteCovering: ISOPROConcreteCovering;
  };
  construction: {
    thrustBearings: {
      numberOfThrustBearings: ISOPROProductDTO["numberOfThrustBearing"];
      numberOfThrustShearPosBearing: ISOPROProductDTO["numberOfThrustShearPosBearing"];
      // numberOfThrustShearNegBearing: ISOPROProductDTO["numberOfThrustShearNegBearing"];
      kindOfThrustBearing: ISOPROProductDTO['kindOfThrustBearing'];
      // dimensionOfThrustShearBearing: ISOPROProductDTO['dimensionOfThrustShearPosBearing'];
    };
    tensionRods: {
      // diameter: ISOPROProductDTO["diameterOfTensionRod"];
      // number: ISOPROProductDTO["numberOfTensionRods"];
    };
  };
};

export type IBendingMomentProperties = IProductProperties & {
  construction: IProductProperties["construction"] & {
    tensionRods: {
      diameter: BendingMomentTypeProductDto["diameterOfTensionRod"];
      number: BendingMomentTypeProductDto["numberOfTensionRods"];
    };
  };
};

